import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Megaphone, Users, Briefcase, Building2, User, AlertCircle, Clock, Calendar, Link, Hash } from 'lucide-react';
import { useBroadcastNotifications } from '@/hooks/useBroadcastNotifications';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

export const BroadcastNotificationForm = () => {
  const { sendBroadcast, isSending } = useBroadcastNotifications();
  
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    link: '',
    target_type: 'all' as 'all' | 'role' | 'department' | 'specific_employees',
    target_value: '',
    priority: 'normal' as 'urgent' | 'high' | 'normal' | 'low',
    expires_at: '',
    channels: ['in_app'] as string[],
    selectedEmployees: [] as string[]
  });

  // Fetch departments
  const { data: departments = [] } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const { data } = await supabase
        .from('employees')
        .select('department')
        .not('department', 'is', null);
      
      const uniqueDepts = [...new Set(data?.map(d => d.department).filter(Boolean))];
      return uniqueDepts;
    }
  });

  // Fetch roles/positions
  const { data: roles = [] } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const { data } = await supabase
        .from('employees')
        .select('position')
        .not('position', 'is', null);
      
      const uniqueRoles = [...new Set(data?.map(r => r.position).filter(Boolean))];
      return uniqueRoles;
    }
  });

  // Fetch employees for specific selection
  const { data: employees = [] } = useQuery({
    queryKey: ['employees-list'],
    queryFn: async () => {
      const { data } = await supabase
        .from('employees')
        .select('id, name, position, department')
        .eq('approval_status', 'approved')
        .order('name');
      
      return data || [];
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.message) {
      return;
    }

    const params: any = {
      title: formData.title,
      message: formData.message,
      link: formData.link || undefined,
      target_type: formData.target_type,
      priority: formData.priority,
      channels: formData.channels
    };

    if (formData.expires_at) {
      params.expires_at = formData.expires_at;
    }

    if (formData.target_type === 'role') {
      params.target_value = formData.target_value;
    } else if (formData.target_type === 'department') {
      params.target_value = formData.target_value;
    } else if (formData.target_type === 'specific_employees') {
      params.target_employees = formData.selectedEmployees;
    }

    sendBroadcast(params);
    
    // Reset form
    setFormData({
      title: '',
      message: '',
      link: '',
      target_type: 'all',
      target_value: '',
      priority: 'normal',
      expires_at: '',
      channels: ['in_app'],
      selectedEmployees: []
    });
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500';
      case 'high': return 'bg-orange-500';
      case 'normal': return 'bg-blue-500';
      case 'low': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const toggleChannel = (channel: string) => {
    setFormData(prev => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter(c => c !== channel)
        : [...prev.channels, channel]
    }));
  };

  const toggleEmployee = (employeeId: string) => {
    setFormData(prev => ({
      ...prev,
      selectedEmployees: prev.selectedEmployees.includes(employeeId)
        ? prev.selectedEmployees.filter(id => id !== employeeId)
        : [...prev.selectedEmployees, employeeId]
    }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Megaphone className="h-5 w-5" />
          إرسال إشعار جماعي
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="title">عنوان الإشعار *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="عنوان مختصر وواضح"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="priority">الأولوية</Label>
              <Select 
                value={formData.priority} 
                onValueChange={(value: any) => setFormData(prev => ({ ...prev, priority: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="urgent">
                    <div className="flex items-center gap-2">
                      <Badge className={`${getPriorityColor('urgent')} text-white`}>عاجل</Badge>
                    </div>
                  </SelectItem>
                  <SelectItem value="high">
                    <div className="flex items-center gap-2">
                      <Badge className={`${getPriorityColor('high')} text-white`}>مرتفع</Badge>
                    </div>
                  </SelectItem>
                  <SelectItem value="normal">
                    <div className="flex items-center gap-2">
                      <Badge className={`${getPriorityColor('normal')} text-white`}>عادي</Badge>
                    </div>
                  </SelectItem>
                  <SelectItem value="low">
                    <div className="flex items-center gap-2">
                      <Badge className={`${getPriorityColor('low')} text-white`}>منخفض</Badge>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">نص الإشعار *</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              placeholder="اكتب رسالة الإشعار هنا..."
              rows={4}
              required
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="link">رابط (اختياري)</Label>
              <div className="relative">
                <Link className="absolute right-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="link"
                  type="url"
                  value={formData.link}
                  onChange={(e) => setFormData(prev => ({ ...prev, link: e.target.value }))}
                  placeholder="https://example.com"
                  className="pr-10"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="expires_at">تاريخ الانتهاء (اختياري)</Label>
              <div className="relative">
                <Calendar className="absolute right-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="expires_at"
                  type="datetime-local"
                  value={formData.expires_at}
                  onChange={(e) => setFormData(prev => ({ ...prev, expires_at: e.target.value }))}
                  className="pr-10"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>الجمهور المستهدف</Label>
            <Select 
              value={formData.target_type} 
              onValueChange={(value: any) => setFormData(prev => ({ ...prev, target_type: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    جميع الموظفين
                  </div>
                </SelectItem>
                <SelectItem value="role">
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    حسب الدور الوظيفي
                  </div>
                </SelectItem>
                <SelectItem value="department">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    حسب القسم
                  </div>
                </SelectItem>
                <SelectItem value="specific_employees">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    موظفين محددين
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.target_type === 'role' && (
            <div className="space-y-2">
              <Label>اختر الدور الوظيفي</Label>
              <Select 
                value={formData.target_value} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, target_value: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="اختر الدور..." />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(role => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {formData.target_type === 'department' && (
            <div className="space-y-2">
              <Label>اختر القسم</Label>
              <Select 
                value={formData.target_value} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, target_value: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="اختر القسم..." />
                </SelectTrigger>
                <SelectContent>
                  {departments.map(dept => (
                    <SelectItem key={dept} value={dept}>
                      {dept}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {formData.target_type === 'specific_employees' && (
            <div className="space-y-2">
              <Label>اختر الموظفين</Label>
              <div className="border rounded-lg p-4 max-h-48 overflow-y-auto">
                {employees.map(employee => (
                  <label key={employee.id} className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.selectedEmployees.includes(employee.id)}
                      onChange={() => toggleEmployee(employee.id)}
                      className="rounded"
                    />
                    <span>{employee.name}</span>
                    {employee.position && (
                      <Badge variant="outline" className="text-xs">
                        {employee.position}
                      </Badge>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>قنوات الإرسال</Label>
            <div className="flex gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.channels.includes('in_app')}
                  onChange={() => toggleChannel('in_app')}
                  className="rounded"
                />
                <span>داخل التطبيق</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.channels.includes('email')}
                  onChange={() => toggleChannel('email')}
                  className="rounded"
                />
                <span>البريد الإلكتروني</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer opacity-50">
                <input
                  type="checkbox"
                  disabled
                  className="rounded"
                />
                <span>رسائل SMS (قريباً)</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="submit" disabled={isSending}>
              {isSending ? 'جاري الإرسال...' : 'إرسال الإشعار'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};